import Analytics from '@/utils/analytics';
import ChevronUpRoundedIcon from '@assets/icons/chevron-up-rounded.svg';
import CreditCardIcon from '@assets/icons/credit-card.svg';
import i18n from '@/utils/i18n';
import React, { useMemo, useRef, useState } from 'react';
import ScissorsIcon from '@assets/icons/scissors.svg';
import { BaseActionSheet } from '@/components/base-action-sheet';
import { Button } from '@/components/button';
import { formatCurrency } from '@/utils/money';
import { List } from '@/components/list';
import { ListOption } from '@/components/list-item';
import { StyleProp, View, ViewStyle } from 'react-native';
import { SvgProps } from 'react-native-svg';
import { ThemedSVGProps } from '@/styles';
import { TrackingData } from '@/utils/analytics/base-reporter';
import { useTheme } from '@/contexts/theme-context';

type PayActionOption = ListOption<{
  Icon: React.ComponentType<SvgProps>;
  iconProps?: ThemedSVGProps;
  onPress: () => void;
  trackingData: TrackingData;
}>;

interface Props {
  containerStyle?: StyleProp<ViewStyle>;
  currencyCode: string;
  disabled?: boolean;
  onPay: () => void;
  onShortPay: () => void;
  subtotalInMicros: number;
}

export const PayActionsButton: React.FC<Props> = ({
  containerStyle,
  currencyCode,
  disabled,
  onPay,
  onShortPay,
  subtotalInMicros,
}) => {
  const theme = useTheme();
  const [isSheetVisible, setIsSheetVisible] = useState(false);
  const buttonRef = useRef<View | null>(null);

  const actions = useMemo<PayActionOption[]>(
    () => [
      {
        metadata: {
          Icon: CreditCardIcon,
          iconProps: { stroke: theme.sk.color.icon.primary },
          onPress: onPay,
          trackingData: { name: 'payments.pay_invoices_button' },
        },
        title: i18n.t('pay_amount_no_separator', {
          paymentAmount: formatCurrency(subtotalInMicros, currencyCode),
        }),
        value: 'PAY',
      },
      {
        metadata: {
          Icon: ScissorsIcon,
          iconProps: { stroke: theme.sk.color.icon.primary },
          onPress: onShortPay,
          trackingData: { name: 'payments.pay_partial_button' },
        },
        title: i18n.t('partial_pay'),
        value: 'SHORT_PAY',
      },
    ],
    [
      currencyCode,
      onPay,
      onShortPay,
      subtotalInMicros,
      theme.sk.color.icon.primary,
    ],
  );

  return (
    <>
      <Button
        accessibilityLabel={i18n.t('payments.open_payment_options')}
        containerStyle={containerStyle}
        disabled={disabled}
        onPress={() => setIsSheetVisible(true)}
        ref={buttonRef}
        rightElement={
          <ChevronUpRoundedIcon
            height={16}
            stroke={theme.sk.color.icon.ondark}
            width={16}
          />
        }
        textProps={{ numberOfLines: 1 }}
        trackingData={null}
      >
        {i18n.t('pay_amount_no_separator', {
          paymentAmount: formatCurrency(subtotalInMicros, currencyCode),
        })}
      </Button>
      {isSheetVisible ? (
        <BaseActionSheet
          contentStyle={SHEET_CONTENT_STYLE}
          isVisible
          onClose={() => setIsSheetVisible(false)}
          popoverTarget={buttonRef.current}
        >
          <List<PayActionOption>
            data={actions}
            onSelect={async (item) => {
              setIsSheetVisible(false);
              if (item.metadata) {
                Analytics.track(
                  `${item.metadata.trackingData.name}.press`,
                  item.metadata.trackingData.properties,
                );
                await item.metadata.onPress();
              }
            }}
            renderLeftComponent={(item) => {
              const { Icon, iconProps } = item.metadata!;
              return <Icon height={24} width={24} {...iconProps} />;
            }}
          />
        </BaseActionSheet>
      ) : null}
    </>
  );
};

const SHEET_CONTENT_STYLE: ViewStyle = {
  paddingHorizontal: 0,
};
